import { useCallback, useEffect, useMemo, useState } from "react";

import { type ChartData } from "chart.js";
import { min } from "lodash";
import moment from "moment";
import { Line } from "react-chartjs-2";
import { type ChartJSOrUndefined } from "react-chartjs-2/dist/types";

import { LoadingSlide } from "@components/title-slide-view";
import { GenericTooltip } from "@components/tooltip-container";
import { useUser } from "@components/user-context";
import { AnnotationsFlyoutButton } from "@features/custom-annotations";
import {
  useModelInsightsChartVisibleDatasets,
  useModelInsightsTimePeriod,
} from "@features/mmm/hooks/mmm-url-state";
import { useCalendarOfEvents } from "@features/mmm/hooks/use-calendar-of-events";
import { useMmmAnnotationDataset } from "@features/mmm/hooks/use-mmm-annotation-dataset";
import { useMmmAnnotations } from "@features/mmm/hooks/use-mmm-annotations";
import { generateDateLabels } from "@features/sales-v3/utils/chart";
import { FETCH_MODEL_INSIGHTS_MODEL_FIT } from "@gql/mmm-model-insights";
import { useFeatureFlags, useNorthbeamQuery } from "@hooks/http";
import {
  type FetchModelInsightsModelFit,
  type FetchModelInsightsModelFitVariables,
} from "@north-beam/nb-client-side";
import { ModelInsightsTrackedComponent } from "@pages/mmm/amplitude-tracking/components";
import { MMMTab } from "@pages/mmm/amplitude-tracking/mmm-event";
import { useTrackMMMEvent } from "@pages/mmm/amplitude-tracking/use-track-mmm-event";
import { useModelMetadata } from "@pages/mmm/hooks/use-model-metadata";
import { useSelectedCycle } from "@pages/mmm/hooks/use-selected-cycle";
import { Checkbox } from "@shared/checkbox";

import { type ModelMetadataKpiFormula } from "../../explore-budgets/client-setting/types";
import {
  ModelPeriodSelector,
  generateDateRange,
} from "../model-period-selector";

import { getChartOptions } from "./model-fit-chart-options";
import { plugins } from "./plugins";
import { setChartData } from "./plugins/chart-data-manager-plugin";
import { setYAxesTitles } from "./plugins/y-scale-titles-plugin";
import {
  MODEL_FIT_ACTUAL_COLOR,
  MODEL_FIT_ACTUAL_ID,
  MODEL_FIT_ACTUAL_LEGEND,
  MODEL_FIT_PREDICTION_BORDER_DASH,
  MODEL_FIT_PREDICTION_COLOR,
  MODEL_FIT_PREDICTION_HDI_ID,
  MODEL_FIT_PREDICTION_HDI_LEGEND,
  MODEL_FIT_PREDICTION_ID,
  MODEL_FIT_PREDICTION_LEGEND,
  MODEL_FIT_SEASONALITY_COLOR,
  MODEL_FIT_SEASONALITY_ID,
  MODEL_FIT_SEASONALITY_LEGEND,
  MODEL_FIT_SPEND_COLOR,
  MODEL_FIT_SPEND_ID,
  MODEL_FIT_SPEND_LEGEND,
} from "./utils/chart-dataset-helpers";

export interface ChartDataPoint {
  x: Date;
  y: number;
}

interface Props {
  selectedKpi: ModelMetadataKpiFormula;
}

export const ModelFitChart = ({ selectedKpi }: Props) => {
  const trackMMMEvent = useTrackMMMEvent(MMMTab.MODEL_INSIGHTS);
  const { selectedCycle } = useSelectedCycle();
  const [modelMetadata] = useModelMetadata();

  const [chartRef, setChartRef] = useState<ChartJSOrUndefined<any>>();
  const setChartInstance = useCallback(
    (instance: ChartJSOrUndefined<any> | null) => {
      if (instance) {
        setChartRef(instance);
      }
    },
    [],
  );

  const { data: modelFitData, loading: modelFitLoading } = useNorthbeamQuery<
    FetchModelInsightsModelFit,
    FetchModelInsightsModelFitVariables
  >(FETCH_MODEL_INSIGHTS_MODEL_FIT, {
    variables: {
      cycleId: selectedCycle.id,
    },
  });
  const dateRange = useMemo(() => {
    return {
      startDate: moment(modelMetadata?.training_start_date).format(
        "YYYY-MM-DD",
      ),
      endDate: moment(modelMetadata?.training_end_date).format("YYYY-MM-DD"),
    };
  }, [modelMetadata]);

  const [selectedDateRange] = useModelInsightsTimePeriod();

  const parsedSelectedDateRange = useMemo(() => {
    if (typeof selectedDateRange === "string") {
      if (selectedDateRange === "all") {
        return {
          startDate: dateRange.startDate,
          endDate: moment().format("YYYY-MM-DD"),
        };
      }
      return generateDateRange(selectedDateRange);
    }
    return {
      startDate: selectedDateRange.startDate,
      endDate: selectedDateRange.endDate,
    };
  }, [dateRange, selectedDateRange]);

  const modelFitKpiData = useMemo(() => {
    if (!modelFitData) {
      return {
        actualData: [],
        baselineData: [],
        predictionData: [],
        upperPredictionData: [],
        lowerPredictionData: [],
      };
    }

    const rawData =
      modelFitData.me.mmm.modelInsightsModelFit[selectedKpi.kpi_name];

    const actualData: ChartDataPoint[] = [];
    const baselineData: ChartDataPoint[] = [];
    const predictionData: ChartDataPoint[] = [];
    const upperPredictionData: ChartDataPoint[] = [];
    const lowerPredictionData: ChartDataPoint[] = [];
    const spendData: ChartDataPoint[] = [];

    const filteredRawData =
      rawData?.filter((row: any) => {
        const date = moment(row.date);
        const { startDate, endDate } = parsedSelectedDateRange;

        return date.isBetween(
          moment(startDate),
          moment(endDate),
          undefined,
          "[]",
        );
      }) ?? [];

    filteredRawData.forEach((row: any) => {
      const date = moment(row.date).toDate();

      actualData.push({ x: date, y: row.data.actual });
      baselineData.push({ x: date, y: row.data.prediction_point_baseline });
      predictionData.push({ x: date, y: row.data.prediction_point_total });
      upperPredictionData.push({ x: date, y: row.data.prediction_band_hi });
      lowerPredictionData.push({ x: date, y: row.data.prediction_band_lo });
      spendData.push({ x: date, y: row.data.spend });
    });

    return {
      actualData,
      baselineData,
      predictionData,
      upperPredictionData,
      lowerPredictionData,
      spendData,
    };
  }, [modelFitData, parsedSelectedDateRange, selectedKpi]);

  const { user } = useUser();

  const { annotationsDataset, loading: isAnnotationsLoading } =
    useMmmAnnotationDataset(
      parsedSelectedDateRange.startDate,
      parsedSelectedDateRange.endDate,
      {
        startDate: dateRange.startDate,
        endDate: moment().format("YYYY-MM-DD"),
      },
      user.timezone,
    );

  const { calendarOfEvents } = useCalendarOfEvents(
    parsedSelectedDateRange.startDate,
    parsedSelectedDateRange.endDate,
    {
      startDate: dateRange.startDate,
      endDate: moment().format("YYYY-MM-DD"),
    },
    user.timezone,
  );

  const [datasetVisibility, setDatasetVisibility] =
    useModelInsightsChartVisibleDatasets();

  const toggleDatasetVisibility =
    (datasetId: keyof typeof datasetVisibility) => (checked: boolean) => {
      trackMMMEvent({
        component: ModelInsightsTrackedComponent.MODEL_FIT_CHART,
        action: "onChange",
        actionInfo: "Toggle dataset visibility",
      });

      setDatasetVisibility((prev: typeof datasetVisibility) => ({
        ...prev,
        [datasetId]: checked,
      }));
    };

  const { hasFeatureFlag } = useFeatureFlags();
  const enableCalendarOfEventsV2 = hasFeatureFlag("enableCalendarOfEventsV2");

  const chartData: ChartData<any, ChartDataPoint[]> = useMemo(() => {
    const knownDates = modelFitKpiData.actualData.map((d) => d.x);
    const minKnownDate = min(knownDates);
    const generatedDates = generateDateLabels(
      parsedSelectedDateRange,
      user.timezone,
      "daily",
    );

    const datasets = [
      {
        label: MODEL_FIT_ACTUAL_ID,
        data: modelFitKpiData.actualData,
        borderColor: MODEL_FIT_ACTUAL_COLOR,
        backgroundColor: MODEL_FIT_ACTUAL_COLOR,
        yAxisID: "y",
        hidden: !datasetVisibility[MODEL_FIT_ACTUAL_ID],
        pointRadius: 0,
        borderWidth: 1.5,
        tension: 0.5,
        animation: false,
      },
      {
        label: MODEL_FIT_PREDICTION_ID,
        data: modelFitKpiData.predictionData,
        borderColor: MODEL_FIT_PREDICTION_COLOR,
        backgroundColor: MODEL_FIT_PREDICTION_COLOR,
        borderDash: MODEL_FIT_PREDICTION_BORDER_DASH,
        yAxisID: "y",
        hidden: !datasetVisibility[MODEL_FIT_PREDICTION_ID],
        pointRadius: 0,
        borderWidth: 1.5,
        tension: 0.5,
        animation: false,
      },
      {
        label: MODEL_FIT_PREDICTION_HDI_ID,
        data: modelFitKpiData.upperPredictionData,
        backgroundColor: "rgba(113,128,254, 0.2)",
        fill: "+1",
        yAxisID: "y",
        hidden: !datasetVisibility[MODEL_FIT_PREDICTION_HDI_ID],
        pointRadius: 0,
        borderWidth: 0,
        tension: 0.5,
        animation: false,
      },
      {
        label: MODEL_FIT_PREDICTION_HDI_ID,
        data: modelFitKpiData.lowerPredictionData,
        yAxisID: "y",
        hidden: !datasetVisibility[MODEL_FIT_PREDICTION_HDI_ID],
        pointRadius: 0,
        borderWidth: 0,
        tension: 0.5,
        animation: false,
      },
      {
        label: MODEL_FIT_SEASONALITY_ID,
        data: modelFitKpiData.baselineData,
        borderColor: MODEL_FIT_SEASONALITY_COLOR,
        backgroundColor: MODEL_FIT_SEASONALITY_COLOR,
        yAxisID: "y",
        hidden: !datasetVisibility[MODEL_FIT_SEASONALITY_ID],
        pointRadius: 0,
        borderWidth: 1.5,
        tension: 0.5,
        animation: false,
      },
      {
        label: MODEL_FIT_SPEND_ID,
        data: modelFitKpiData.spendData,
        borderColor: MODEL_FIT_SPEND_COLOR,
        backgroundColor: MODEL_FIT_SPEND_COLOR,
        yAxisID: MODEL_FIT_SPEND_ID,
        hidden: !datasetVisibility[MODEL_FIT_SPEND_ID],
        pointRadius: 0,
        borderWidth: 1.5,
        tension: 0.5,
        animation: false,
      },
    ];

    if (!enableCalendarOfEventsV2) {
      datasets.push(annotationsDataset as any);
    }

    return {
      labels: generatedDates.filter((date) =>
        minKnownDate ? date >= minKnownDate : true,
      ),
      datasets,
    };
  }, [
    parsedSelectedDateRange,
    user.timezone,
    enableCalendarOfEventsV2,
    modelFitKpiData.actualData,
    modelFitKpiData.predictionData,
    modelFitKpiData.upperPredictionData,
    modelFitKpiData.lowerPredictionData,
    modelFitKpiData.baselineData,
    modelFitKpiData.spendData,
    datasetVisibility,
    annotationsDataset,
  ]);

  useEffect(() => {
    const outOfSampleCutoffDate = moment(
      modelMetadata?.out_of_sample_cutoff_date,
    ).toDate();

    const getStringOfReasonableLength = (val: string) => {
      if (val.length > 25) {
        return val.substring(0, 22) + "...";
      }

      return val;
    };

    if (chartRef) {
      setChartData(chartRef, {
        data: chartData,
        inSampleData: {
          startDate: moment(modelMetadata?.training_start_date).toDate(),
          endDate: outOfSampleCutoffDate,
        },
        outOfSampleData: {
          startDate: outOfSampleCutoffDate,
          endDate: moment(modelMetadata?.training_end_date).toDate(),
        },
        pageConfiguration: {
          timezone: user.timezone,
        },
        dateRange: parsedSelectedDateRange,
        // Effectively disables the plugin if calendarOfEvents is empty
        calendarOfEvents: enableCalendarOfEventsV2 ? calendarOfEvents : [],
      });
      setYAxesTitles({
        chart: chartRef,
        naming: {
          [MODEL_FIT_SPEND_ID]: "Spend",
          y: getStringOfReasonableLength(selectedKpi.friendly_name),
        },
      });
    }
  }, [
    chartRef,
    chartData,
    modelMetadata,
    user.timezone,
    parsedSelectedDateRange,
    selectedKpi.friendly_name,
    calendarOfEvents,
    enableCalendarOfEventsV2,
  ]);

  if (modelFitLoading || isAnnotationsLoading) {
    return <LoadingSlide />;
  }

  return (
    <div className="pb-4">
      <div className="mb-4">
        <ModelPeriodSelector />
      </div>
      <div className="flex items-center justify-between">
        <div className="font-bold text-sm">{selectedKpi.friendly_name}</div>
        <div className="flex justify-between text-xs space-x-5">
          <label className="flex items-center space-x-0.5 cursor-pointer">
            <Checkbox
              className="mr-2"
              value={datasetVisibility[MODEL_FIT_ACTUAL_ID]}
              onChange={toggleDatasetVisibility(MODEL_FIT_ACTUAL_ID)}
            />
            <div
              dangerouslySetInnerHTML={{ __html: MODEL_FIT_ACTUAL_LEGEND }}
            />
            <div>Actual</div>
          </label>
          <label className="flex items-center space-x-0.5 cursor-pointer">
            <Checkbox
              className="mr-2"
              value={datasetVisibility[MODEL_FIT_PREDICTION_ID]}
              onChange={toggleDatasetVisibility(MODEL_FIT_PREDICTION_ID)}
            />
            <div
              dangerouslySetInnerHTML={{
                __html: MODEL_FIT_PREDICTION_LEGEND,
              }}
            />
            <div>Predicted</div>
          </label>
          <label className="flex items-center space-x-0.5 cursor-pointer">
            <Checkbox
              className="mr-2"
              value={datasetVisibility[MODEL_FIT_PREDICTION_HDI_ID]}
              onChange={toggleDatasetVisibility(MODEL_FIT_PREDICTION_HDI_ID)}
            />
            <div
              dangerouslySetInnerHTML={{
                __html: MODEL_FIT_PREDICTION_HDI_LEGEND,
              }}
            />
            <div>
              Predicted ({(modelMetadata?.hdi_prob || 0) * 100}% HDI)
              <GenericTooltip
                content={
                  <>
                    <div>The model expects X% of performance actuals </div>
                    <div>to fall within the provided range</div>
                  </>
                }
              />
            </div>
          </label>
          <label className="flex items-center space-x-0.5 cursor-pointer">
            <Checkbox
              className="mr-2"
              value={datasetVisibility[MODEL_FIT_SEASONALITY_ID]}
              onChange={toggleDatasetVisibility(MODEL_FIT_SEASONALITY_ID)}
            />
            <div
              dangerouslySetInnerHTML={{
                __html: MODEL_FIT_SEASONALITY_LEGEND,
              }}
            />
            <div>
              Baseline
              <GenericTooltip
                content={
                  <>
                    <div>A visual representation of predicted changes</div>
                    <div>based on previous calendar years</div>
                  </>
                }
              />
            </div>
          </label>
          <label className="flex items-center space-x-0.5 cursor-pointer">
            <Checkbox
              className="mr-2"
              value={datasetVisibility[MODEL_FIT_SPEND_ID]}
              onChange={toggleDatasetVisibility(MODEL_FIT_SPEND_ID)}
            />
            <div dangerouslySetInnerHTML={{ __html: MODEL_FIT_SPEND_LEGEND }} />
            <div>Spend</div>
          </label>
        </div>
        <AnnotationsFlyoutButton
          dateRange={parsedSelectedDateRange}
          buttonText="CALENDAR OF EVENTS"
        />
      </div>
      <div className="h-96">
        <Line
          id="model-fit-chart"
          ref={setChartInstance}
          options={getChartOptions(
            user,
            selectedKpi.friendly_name,
            modelMetadata?.hdi_prob ?? 0 * 100,
            selectedKpi.kpi_type,
            chartData,
          )}
          data={chartData}
          plugins={plugins}
        />
      </div>
    </div>
  );
};
